import * as React from "react";
import { parse } from "csv/browser/esm";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Modal, ModalSize } from "../../misc/components/modal";
import { FileDownload } from "../../misc/FileDownload";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { FileCreation, File as FileModel } from "webportalclient";
import AsyncActionButton from "../../misc/components/AsyncActionButton";
import { DateTimeAccuracy, LocalizedDateTime } from "../../misc/components/LocalizedDateTime";
import { escapeEncode } from "../../misc/api/ODataHelpers";

interface VerifyAccessRequestsModalProps {
    onClose: () => void;
}

interface VerifyAccessLine {
    lineNumber: number;
    record: any;
}

enum CsvColumns {
    Email = "Email",
    Roles = "Roles",
    SiteNumbers = "SiteNumbers",
    SP6Group = "SpirotracVIGroup"
}

export const VerifyAccessRequestsModal = ({ onClose }: VerifyAccessRequestsModalProps) => {
    const [fileDelimiter, setFileDelimiter] = React.useState<string>(",");
    const [recordDelimiter, setRecordDelimiter] = React.useState<string>("|");
    const [selectedFile, setSelectedFile] = React.useState<File>();
    const [error, setError] = React.useState<string | undefined>();
    const [warning, setWarning] = React.useState<string | undefined>();
    const [submitting, setSubmitting] = React.useState(false);
    const [message, setMessage] = React.useState<string | undefined>();
    const [verifyFile, setVerifyFile] = React.useState<FileModel>();
    const client = useWebPortalApiClient();

    React.useEffect(() => {
        const getVerifyingFile = async () => {
            const existingFile = await client.client.getFiles(undefined, "fileName eq 'AccessRequestVerifyCsv.csv' and not studyIds/any()");

            setVerifyFile(existingFile.value[0]);
        }

        getVerifyingFile();
    }, []);

    const onDownloadTemplate = async () => {
        const dataTemplate = `${CsvColumns.Email}${fileDelimiter}${CsvColumns.Roles}${fileDelimiter}${CsvColumns.SiteNumbers}${fileDelimiter}${CsvColumns.SP6Group}`;

        const dataTemplateComment = `\n# Roles must be specified in the format (xxx) yyy where x is the study name and y is the role name such as (Study 1) Role 1
# Sites follow a similar format where x is the study name and y is the site number for example (Study 1) 100
# Spirotrac VI Group are one of the values: none; admin; tech; site admin`;

        const templateData = new Blob([dataTemplate + dataTemplateComment], { type: "text/csv" });

        FileDownload(templateData, "VerifyAccessRequestsTemplate.csv", "text/csv");
    }

    const findDuplicates = (arr) => {
        const sorted = arr.slice().sort(x => x[CsvColumns.Email].toLowerCase());

        const results = [];
        for (let i = 0; i < sorted.length - 1; i++) {
            if (sorted[i + 1][CsvColumns.Email].toLowerCase() == sorted[i][CsvColumns.Email].toLowerCase()) {
                results.push(sorted[i][CsvColumns.Email]);
            }
        }
        return results;
    }

    const resetFormState = () => {
        setMessage(undefined);
        setError(undefined);
        setWarning(undefined);
    }

    const onSaveClick = async () => {
        const reader = new FileReader();

        reader.onload = async (event) => {
            setSubmitting(true);
            resetFormState();

            const file = event.target.result as string;

            parse(file, {
                delimiter: fileDelimiter,
                columns: [CsvColumns.Email, CsvColumns.Roles, CsvColumns.SiteNumbers, CsvColumns.SP6Group],
                from_line: 2,
                comment: "#",
                on_record: (record, { lines }) => ({ lineNumber: lines, record: record })
            }, async (err, data: VerifyAccessLine[]) => {
                if (err) {
                    setSubmitting(false);
                    setError(err.message);
                    return;
                }

                try {
                    const dups = findDuplicates(data.map(x => x.record));

                    if (dups.length > 0) {
                        throw new Error(`Duplicate emails found: ${dups.join(",")}`)
                    }

                    var knownSP6Groups = ["none", "admin", "tech", "site admin"];

                    for (const line of data) {
                        try {
                            const roleRecords = line.record[CsvColumns.Roles].split(recordDelimiter);

                            for (const roleRecord of roleRecords) {
                                const roleRegExp = new RegExp(/\((?<Study>.+)\)\s(?<Role>.+)/);
                                const roleSearchResult = roleRegExp.exec(roleRecord);

                                if (roleSearchResult?.input === "(All Studies) Administrator")
                                    throw new Error(`Administrator users cannot be verified with this method. Please manually approve instead.`);

                                if (roleSearchResult?.groups?.Study === null || roleSearchResult?.groups?.Study === undefined || roleSearchResult?.groups?.Role === null || roleSearchResult?.groups?.Role === null)
                                    throw new Error(`Roles must be specified in the format '(Study Name) Role Name'`);

                                const study = await client.client.getStudies("id", `name eq '${escapeEncode(roleSearchResult.groups.Study)}'`);
                                if (study.value.length === 0)
                                    throw new Error(`Study '${roleSearchResult?.groups?.Study}' does not exist.`);
                                const role = await client.client.getRoles("id,name", `name eq '${escapeEncode(roleSearchResult.groups.Role)}' and studyId eq ${study.value[0].id}`);

                                if (role.value.length === 0) {
                                    throw Error(`Role '${roleSearchResult.groups.Role}' does not exist in study '${roleSearchResult.groups.Study}'`);
                                }
                            }

                            const siteRecords = line.record[CsvColumns.SiteNumbers].split(recordDelimiter);

                            if (siteRecords[0] !== "") {
                                for (const siteRecord of siteRecords) {
                                    const siteRegExp = new RegExp(/\((?<Study>.+)\)\s(?<Site>.+)/);
                                    const siteSearchResult = siteRegExp.exec(siteRecord)

                                    if (siteSearchResult?.groups?.Study === null || siteSearchResult?.groups?.Study === undefined || siteSearchResult?.groups?.Role === null || siteSearchResult?.groups?.Role === null)
                                        throw new Error(`Sites must be specified in the format '(Study Name) Role Name'`);

                                    const study = await client.client.getStudies("id", `name eq '${escapeEncode(siteSearchResult.groups.Study)}'`);
                                    const site = await client.client.getSites("id,number", `number eq '${escapeEncode(siteSearchResult.groups.Site)}' and studyId eq ${study.value[0].id}`);

                                    if (site.value.length === 0) {
                                        throw Error(`Site '${siteSearchResult.groups.Site}' does not exist in study '${siteSearchResult.groups.Study}'`);
                                    }
                                }
                            }

                            let sp6Group = line.record[CsvColumns.SP6Group];

                            if (sp6Group != null) {
                                sp6Group = sp6Group.trim().toLowerCase();

                                if (sp6Group != "" && knownSP6Groups.findIndex(e => e == sp6Group) == -1)
                                    throw Error(`Spirotrac VI Group must be one of the known groups. please see template file example form more details.`);
                            }

                        } catch (ex) {
                            throw Error(`Line ${line.lineNumber} - ${ex.message}`);
                        }
                    }

                    let binaryString = '';
                    const bytes = new Uint8Array(await selectedFile.arrayBuffer());
                    const length = bytes.byteLength;
                    for (let i = 0; i < length; i++) {
                        binaryString += String.fromCharCode(bytes[i]);
                    }

                    if (verifyFile) {
                        await client.client.deleteFile(verifyFile.id);
                    }

                    const fileResult = await client.client.postFile({
                        fileContent: btoa(binaryString),
                        fileName: "AccessRequestVerifyCsv.csv",
                        contentType: selectedFile.type,
                        fileSizeInBytes: selectedFile.size,
                    } as FileCreation);
                    if (fileResult)
                        setVerifyFile(fileResult);
                        setMessage(`CSV file successfully uploaded. Access Request approval will begin in the background.`);
                } catch (ex) {
                    setSubmitting(false);
                    setError(ex.message ?? ex.error.message);
                } finally {
                    setSubmitting(false);
                }
            })
        }

        reader.readAsText(selectedFile);
    }

    return (
        <Modal show={true} size={ModalSize.Normal} header="Verify Access Requests">
            {message ? <Alert show={true} category={AlertCategoryEnum.Success}>{message}</Alert> : null}
            <div className="row">
                <div className="col-md-12">
                    <div className="panel-body">
                        {warning ? <Alert show={true} category={AlertCategoryEnum.Warning}>{warning}</Alert> : null}
                        {error ? <Alert show={true} category={AlertCategoryEnum.Danger}>{error}</Alert> : null}
                        
                        <div className="row">
                            <div className="form-group col-sm-12">
                                <label htmlFor="sitesFile">CSV File</label>
                                <input id="sitesFile" type="file" accept="text/csv" className="form-control" onChange={(e) => { resetFormState(); setSelectedFile(e.target.files[0]) }} />
                            </div>
                        </div>
                        {verifyFile ?
                            <div className="row">
                                <div className="col-md-12">File last uploaded: <LocalizedDateTime time={verifyFile.lastModified} accuracy={DateTimeAccuracy.Minute} /></div>
                            </div>: null}
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                <AsyncActionButton id="importButton" className="btn btn-primary" type="submit" onClick={onSaveClick} isSubmitting={submitting}>
                    Upload
                </AsyncActionButton>&nbsp;
                <button id="downloadTemplateButton" className="btn btn-primary btn-outline" disabled={submitting} onClick={() => onDownloadTemplate()}>
                    Download Template
                </button>
                <button id="returnToListButton" className="btn btn-primary btn-outline" disabled={submitting} onClick={onClose}>
                    Return to List
                </button>
            </div>
        </Modal>
    );
}
